import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultTextField from 'components/Utils/DefaultTextField';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import { api } from 'careand-redux/api/ApiThunks';
import './style.scss';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';
import DocumentTemplate from 'components/HealthRecords/Documents/DocumentTemplate';
import moment from 'moment-timezone';
import PerformanceUtils from 'services/PerformanceUtils';
import _ from 'lodash';
import { UserRoleEnum } from 'generated/api';
import ConfirmationButton from '../../../Utils/NewDefaultButton/ConfirmationButton';

const NewPrefilledDocumentTemplate = ({ selectedTemplate, isOwn, onRequestClose }) => {
  const [selected, setSelected] = useState(selectedTemplate);
  const prefilled = useAppSelector((state) => state.templates.templatesPrefilled);

  // Expose a close handler that navigates back to list first, then closes the modal
  useEffect(() => {
    if (onRequestClose) {
      onRequestClose.current = selected ? () => setSelected(undefined) : null;
    }
  }, [selected, onRequestClose]);

  const sorted = useMemo(() => {
    if (!isOwn) {
      return _(prefilled)
        .filter((it) => it.physician_id === 'none')
        .sortBy('title')
        .value();
    }
    return _.sortBy(prefilled, 'title');
  }, [prefilled, isOwn]);

  const bundled = useMemo(() => {
    const bundles = [];
    sorted.forEach((template) => {
      const matchingBundle = template.bundle && bundles.find((it) => it.title === template.bundle);
      if (matchingBundle) {
        matchingBundle.templates.push(template);
      } else {
        bundles.push({
          title: template.bundle ?? template.title,
          templates: [template],
        });
      }
    });
    return bundles;
  }, [sorted]);

  return (
    <div className="new-prefilled-document-template">
      {!selected && (
        <div className="list scroll-list">
          <div className="list-content">
            <div className="small-title">Prefilled Templates</div>
            {sorted.length === 0 && (
              <span key="no-element" className="block pt-2">
                No items to show
              </span>
            )}
            {bundled.map((bundle) => {
              const inBundle = bundle.templates.length > 1;
              return (
                <div className="bundle-wrapper" key={bundle.title}>
                  {inBundle && (
                    <div className="bundle-main">{bundle.title}&nbsp;Bundle</div>
                  )}
                  {bundle.templates.map((template) => (
                    <div
                      className={`template${inBundle ? ' in-bundle' : ''}`}
                      key={template.id}
                      onClick={() => setSelected(template)}
                    >
                      {template.title}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          <div>
            <DefaultButton
              name={'Add New'}
              className="blue"
              icon="plus"
              onClick={() => setSelected({})}
            />
          </div>
        </div>
      )}
      {selected && (
        <EditPrefilled
          key={selected.id}
          item={selected}
          onClose={() => setSelected()}
          isOwn={isOwn}
        />
      )}
    </div>
  );
};

const DEFAULT_OBJECT = {};

const EditPrefilled = ({ item = DEFAULT_OBJECT, onClose, isOwn }) => {
  const dispatch = useAppDispatch();
  const templates = useAppSelector((state) => state.templates.templates);
  const prefilled = useAppSelector((state) => state.templates.templatesPrefilled);

  const defaultTitle = useMemo(() => {
    return templates.find((template) => template.id === item.template_id)?.description;
  }, [item.template_id, templates]);

  const [formId] = useState(moment().unix());
  const [values, setValues] = useState(item);
  const [templateSearch, setTemplateSearch] = useState(defaultTitle);
  const [bundle, setBundle] = useState(item.bundle);

  const isDeleting = useIsLoading(api.Templates.deleteDocumentTemplatesPrefilled);
  const isAdding = useIsLoading(api.Templates.addDocumentTemplatePrefilled);
  const isPhysician = useAppSelector(
    (state) => state.authentication.role === UserRoleEnum.Physician,
  );

  function onChange(event) {
    setValues({ ...values, [event.target.name]: event.target.value });
  }

  const templateId = useMemo(() => {
    return templates.find((template) => template.description === templateSearch)?.id;
  }, [templateSearch, templates]);

  async function onSave() {
    const documentTemplatePrefilled = {
      id: values.id,
      title: values.title,
      template_id: templateId,
      values: values.values || [],
      bundle: bundle || undefined,
    };

    let response = await dispatch(
      api.Templates.addDocumentTemplatePrefilled({ documentTemplatePrefilled, isOwn }),
      {
        errorTitle: 'Prefilled Template',
      },
    );
    if (response) {
      onClose();
    }
  }

  async function onDelete() {
    await dispatch(api.Templates.deleteDocumentTemplatesPrefilled({ id: values.id }), {
      errorTitle: 'Prefilled Template',
    });
    onClose();
  }

  const templateTitles = useMemo(() => {
    return templates.map((template) => template.description);
  }, [templates]);

  const templateBundles = useMemo(() => {
    return _.uniq(prefilled.map((it) => it.bundle).filter(Boolean));
  }, [prefilled]);

  const saveDisabled = !templateId || !values.title;

  const prefilledValues = useMemo(() => {
    let result = {};
    (values.values || []).forEach((val) => {
      result[val.id] = val.value;
    });
    return result;
  }, [values.values]);

  function onChangeFields(event) {
    let fields = PerformanceUtils.clone(values.values || []);
    let index = fields.indexOf((field) => field.id === event.target.name);
    if (index >= 0) {
      fields[index] = event.target.value;
    } else {
      fields.push({ id: event.target.name, value: event.target.value });
    }
    setValues({ ...values, values: fields });
  }

  return (
    <div className="prefilled-template-info">
      <div className="fields scroll-list">
        <div className="small-title">
          <span className="clickable" onClick={onClose}>
            All Templates
          </span>{' '}
          &gt; Prefilled Template Details
        </div>
        <DefaultTextField label="Title" name="title" value={values.title} onChange={onChange} />

        <DefaultAutoComplete
          style={{ width: '100%' }}
          name="templateSearch"
          label="Template"
          placeholder="Select template"
          input={{
            value: templateSearch || '',
            onChange: (text) => setTemplateSearch(text),
          }}
          onUpdateInput={(text) => setTemplateSearch(text)}
          dataSource={templateTitles}
        />

        <DefaultAutoComplete
          style={{ width: '100%' }}
          name="bundle"
          label="Add To Bundle"
          placeholder="Bundle Name"
          input={{
            value: bundle || '',
            onChange: (text) => setBundle(text),
          }}
          onUpdateInput={(text) => setBundle(text)}
          dataSource={templateBundles}
        />
        <span className="add-to-bundle-info">Enter a unique name to create a new bundle.</span>

        <div className="button-wrapper">
          <ConfirmationButton
            name="Delete"
            className="red"
            icon="trash"
            onClick={onDelete}
            hide={!values.id}
            loading={isDeleting}
            confirmation={{
              message:
                'This cannot be reversed. Are you sure you want to delete this prefilled template?',
            }}
          />
          <DefaultButton
            name="Save"
            className="blue"
            icon="check"
            onClick={onSave}
            disabled={saveDisabled}
            loading={isAdding}
          />
        </div>
        {isOwn && (
          <span className="physician-only-info">
            This prefilled template will only be available to you.
          </span>
        )}
      </div>
      {templateId && (
        <div className="template scroll-list">
          <DocumentTemplate
            key={templateId}
            templateId={templateId}
            templateOnly={true}
            prefilledValues={prefilledValues}
            onChangeFields={onChangeFields}
            form={formId}
            hideDocumentPanel
            forceHideButtons
          />
        </div>
      )}
    </div>
  );
};

NewPrefilledDocumentTemplate.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default NewPrefilledDocumentTemplate;
